import React from 'react'
import { Text, Flex, Box, Link, Icon, Avatar, useToast, useTheme } from '@homebotapp/hb-react-component-catalog'
import { useSelector } from 'react-redux'
import { Link as ReactRouterLink } from 'react-router-dom'
import { defineMessages, useIntl } from 'react-intl'
import { PATH_HELPERS } from '../../../constants/navigation'
import { selectClientId } from '../../../store/selectors/client'
import { formatAbbrCurrency, formatNumber } from '../../../helpers/formatters'
import { selectCustomerProfile } from '../../../store/selectors/customerProfile'
import { DmModal } from '../../../Components/shared/DmModal/DmModal'
import { useSendMessage } from '../../../hooks/useSendMessage'
import { CUSTOMER_MESSAGES } from '../../../constants/customerMessages'
import useViewport from '../../../hooks/useViewport'
import { useGetMarketData } from '../hooks/useGetMarketData'
import { GeoAreaLevel } from '../../../api/gqlaxy/gql/generated/graphql'
import { hasCustomerToken } from '../../../auth'
import { StatBox, TemperatureIndicator, MarketLabels } from '../../../Components/shared/MarketTemperature'

interface MarketTemperatureIndicatorProps {
  zipCode: string
  onSendMessageComplete?: () => void
}

export const MSG = defineMessages({
  mapView: {
    id: 'MarketTemperature.mapView',
    defaultMessage: 'Map view'
  },
  marketOverview: {
    id: 'MarketTemperature.marketOverview',
    defaultMessage: 'Market Overview'
  },
  averagePrice: {
    id: 'MarketTemperature.averagePrice',
    defaultMessage: 'Average price'
  },
  sizeInBudget: {
    id: 'MarketTemperature.sizeInBudget',
    defaultMessage: 'Size in your budget'
  },
  buyingTimeline: {
    id: 'MarketTemperature.buyingTimeline',
    defaultMessage: 'Buying Timeline'
  },
  askYourAgent: {
    id: 'MarketTemperature.askYourAgent',
    defaultMessage: 'Ask {agentName} a question'
  },
  howIsTheMarket: {
    id: 'MarketTemperature.howIsTheMarket',
    defaultMessage: 'How is the market in {zipCode}?'
  },
  messageSent: {
    id: 'MarketTemperature.messageSent',
    defaultMessage: 'Message sent successfully!'
  },
  messageNotSent: {
    id: 'MarketTemperature.messageNotSent',
    defaultMessage: 'Message could not be sent.'
  }
})

export const MarketTemperatureIndicatorV2: React.FC<MarketTemperatureIndicatorProps> = ({
  zipCode,
  onSendMessageComplete
}) => {
  const viewport = useViewport()
  const isImpersonating = hasCustomerToken()
  const intl = useIntl()
  const toast = useToast()
  const clientId = useSelector(selectClientId)
  const [showModal, setShowModal] = React.useState(false)

  const { mutateAsync: sendMessage } = useSendMessage()

  const { data: marketData } = useGetMarketData(
    {
      input: {
        geoArea: {
          level: GeoAreaLevel.PostalCode,
          zipCode,
          name: '',
          stateAbbreviation: ''
        },
        clientId: clientId as string
      }
    },
    {
      enabled: !!zipCode && !!clientId
    }
  )

  const { marketData: data } = marketData || {}

  const { theme } = useTheme()

  const customerProfile = useSelector(selectCustomerProfile) || {}

  const askYourAgent = async () => {
    const result = await sendMessage({
      input: {
        clientId: clientId as string,
        topicKey: CUSTOMER_MESSAGES.HOW_IS_MARKET.topicKey,
        topic: CUSTOMER_MESSAGES.HOW_IS_MARKET.topic,
        message: intl.formatMessage(CUSTOMER_MESSAGES.HOW_IS_MARKET.message, {
          zipCode
        })
      }
    })
    if (result.sendMessage) {
      setShowModal(false)
      onSendMessageComplete?.()
      toast({
        title: intl.formatMessage(MSG.messageSent),
        status: 'success'
      })
    } else {
      toast({
        title: intl.formatMessage(MSG.messageNotSent),
        status: 'error'
      })
    }
  }

  return (
    <>
      <Box
        width='100%'
        margin='0 auto'
        mt={8}
        bgColor='neutral.200'
        border='1px solid'
        borderColor='neutral.300'
        borderRadius='16px'
        mb={12}
      >
        <Flex direction='column' py={4} px={viewport.isSmall ? 0 : 6}>
          <Flex mb={4} px={!viewport.isSmall ? 0 : 4} justifyContent='space-between' alignItems='center'>
            <Text size='lg' fontWeight='bold' color='neutral.900' mb={0}>
              {intl.formatMessage(MSG.marketOverview)}
            </Text>
            <Link
              display='flex'
              alignItems='center'
              fontWeight='semibold'
              as={ReactRouterLink}
              to={
                zipCode
                  ? PATH_HELPERS.zip.buildPath(clientId, zipCode)
                  : PATH_HELPERS.markets.buildPath(clientId, '/explore/map')
              }
              size='m'
            >
              <Icon mr={2} name='map-marked-alt-filled' />
              {intl.formatMessage(MSG.mapView)}
            </Link>
          </Flex>

          <Box padding={4}>
            <TemperatureIndicator marketTemp={data?.marketTemp} />
            <MarketLabels />
            <Flex
              justifyContent='center'
              gap={viewport.isSmall ? '12px' : 10}
              flexDirection={viewport.isSmall ? 'column' : 'row'}
            >
              {data?.avgPrice && (
                <StatBox
                  icon='💵'
                  label={intl.formatMessage(MSG.averagePrice)}
                  value={formatAbbrCurrency(data?.avgPrice)}
                />
              )}
              {data?.sqftAfford && (
                <StatBox
                  icon='🏠'
                  label={intl.formatMessage(MSG.sizeInBudget)}
                  value={`${formatNumber(data?.sqftAfford)} SQFT`}
                />
              )}
              {data?.buyingTimeline && (
                <StatBox
                  icon='🚛'
                  label={intl.formatMessage(MSG.buyingTimeline)}
                  value={`${data?.buyingTimeline} days`}
                />
              )}
            </Flex>
          </Box>
        </Flex>
        <Flex
          cursor='pointer'
          onClick={() => {
            if (!isImpersonating) {
              setShowModal(true)
            }
          }}
          height='57px'
          bgColor='whiteAlpha.100'
          justifyContent='center'
          alignItems='center'
        >
          <Avatar
            size='sm'
            src={customerProfile.photoUri}
            style={{
              border: '2px solid #fff',
              boxShadow: '0 0 0 2px rgba(0, 0, 0, 0.1)'
            }}
          />
          <Text size='sm' fontWeight='bold' color='neutral.900' textAlign='center' mt={2} ml={2}>
            {intl.formatMessage(MSG.askYourAgent, {
              agentName: `${customerProfile.firstName} ${customerProfile.lastName}`
            })}
          </Text>
        </Flex>
      </Box>
      <DmModal
        onClose={() => {
          setShowModal(false)
        }}
        isOpen={showModal}
        defaultMessage={intl.formatMessage(MSG.howIsTheMarket, {
          zipCode
        })}
        avatarSrc={customerProfile.photoUri}
        name={`${customerProfile.firstName} ${customerProfile.lastName}`}
        onSubmit={askYourAgent}
      />
    </>
  )
}
